import React, { useState, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Alert,
} from 'react-native';
import * as Font from 'expo-font';
import { useAuthenticator } from '@aws-amplify/ui-react'; // <-- import this
import { apiUrl } from './utils/config';
import { fetchAuthSession } from 'aws-amplify/auth';

const DeleteAccountPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [deleteRequested, setDeleteRequested] = useState(false);

  // Grab signOut from Amplify's useAuthenticator
  const { signOut } = useAuthenticator();

  // Load custom font if desired
  const loadFonts = async () => {
    await Font.loadAsync({
      'Futura': require('./assets/fonts/futura.ttf'),
    });
  };
  
  useEffect(() => {
    loadFonts();
  }, []);

  const handleDeletePress = () => {
    setShowConfirmation(true);
  };

  const confirmDeletion = async () => {
    setShowConfirmation(false);
    
    try {
      // 1) Retrieve Auth Token
      const authToken = await getAuthToken();

      // 2) Make the API call to delete user
      const response = await fetch(`${apiUrl}admin/delete-user-account`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const errorMsg = errorData?.message || 'Unable to delete account';
        throw new Error(errorMsg);
      }

      // 3) If successful, sign the user out and show confirmation message
      await signOut();               // <--- Sign them out right away
      setDeleteRequested(true);
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to delete account. Please try again.');
    }
  };

  const cancelDeletion = () => {
    setShowConfirmation(false);
  };

  // Utility to fetch the current ID token
  const getAuthToken = useCallback(async (): Promise<any | null> => {
      try {
        const session = await fetchAuthSession();
        return session?.tokens?.idToken || null;
      } catch (error) {
        console.error('Error retrieving auth token:', error);
        return null;
      }
    }, []);

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.innerContainer}>
          {/* If account isn’t yet requested for deletion */}
          {!deleteRequested && (
            <>
              <Text style={styles.title}>We’re sorry to see you go</Text>

              <View style={styles.buttonContainer}>
                <View style={styles.buttonShadowBackground} />
                <TouchableOpacity
                  onPress={handleDeletePress}
                  style={[
                    styles.button,
                  ]}
                >
                  <Text style={styles.buttonText}>Delete My Account</Text>
                </TouchableOpacity>
              </View>

              {/* Confirmation Block */}
              {showConfirmation && (
                <View style={styles.confirmationBox}>
                  <Text style={styles.confirmationText}>
                    Are you sure you want to delete your account?
                    All of your data will be permanently removed.
                  </Text>
                  <Text style={styles.confirmationText}>
                    Your account and data will be deleted within 90 days.
                  </Text>

                  <View style={styles.confirmationButtons}>
                    <TouchableOpacity style={styles.confirmButton} onPress={confirmDeletion}>
                      <Text style={styles.buttonText}>Yes, Delete</Text>
                    </TouchableOpacity>

                    <TouchableOpacity style={styles.cancelButton} onPress={cancelDeletion}>
                      <Text style={styles.buttonText}>Cancel</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              )}
            </>
          )}

          {/* If the request to delete was submitted */}
          {deleteRequested && (
            <View style={styles.deletedContainer}>
              <Text style={styles.deletedText}>
                Your request to delete your account has been received.
              </Text>
              <Text style={styles.deletedText}>
                We will remove your account and all associated data within 90 days.
              </Text>
              <Text style={styles.deletedText}>
                You have been signed out.
              </Text>
            </View>
          )}
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

// ================== STYLES ==================
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F7F8FB',
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',  // Centers content vertically
    alignItems: 'center',       // Centers content horizontally
  },
  innerContainer: {
    alignItems: 'center',
    width: '100%',
    paddingHorizontal: 20,
  },
  title: {
    fontSize: 60,               // "Super big" title
    maxWidth: 600,
    textAlign: 'center',
    fontFamily: 'Futura',       // If using a custom font
    fontWeight: 'bold',
    marginBottom: '5%',
  },
  inputContainer: {
    backgroundColor: '#D9D9D9',
    borderRadius: 20,
    marginBottom: 20,
    width: '100%',
    maxWidth: 300,              // Restricts the field from becoming too wide
    paddingHorizontal: 15,
    alignSelf: 'center',
  },
  input: {
    fontFamily: 'Futura',
    fontSize: 14,
    textAlign: 'center',
    fontWeight: 'bold',
    padding: 20,
    ...Platform.select({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
  buttonContainer: {
    alignSelf: 'center',
  },
  buttonShadowBackground: {
    position: 'absolute',
    width: 238,
    height: 55,
    backgroundColor: '#AFC9BF',
    borderRadius: 16,
    top: '25%',
    left: '1%',
  },
  button: { 
    backgroundColor: '#544564', 
    padding: 15, 
    borderRadius: 15, 
    alignItems: 'center', 
    alignSelf: 'center',
    width: 238,
    height: 55,
    marginTop: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  buttonText: { 
    fontFamily: 'Futura',
    color: '#FFF', 
    fontSize: 16 
  },
  confirmationBox: {
    backgroundColor: '#fff',
    borderRadius: 15,
    padding: 20,
    marginTop: 30,
    width: '100%',
    maxWidth: 300,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 3,
    elevation: 2,
  },
  confirmationText: {
    textAlign: 'center',
    fontFamily: 'Futura',
    fontSize: 16,
    marginBottom: 10,
    color: '#333',
  },
  confirmationButtons: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 15,
  },
  confirmButton: {
    backgroundColor: '#B32121',
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 20,
  },
  cancelButton: {
    backgroundColor: '#aaa',
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 20,
  },
  deletedContainer: {
    marginTop: 30,
    alignItems: 'center',
    maxWidth: 300,
  },
  deletedText: {
    textAlign: 'center',
    fontFamily: 'Futura',
    fontSize: 16,
    marginBottom: 15,
    color: '#333',
  },
});

export default DeleteAccountPage;
